import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import CustomRightModal from '../../Elements/modal/CustomRightModal'
import { CounterText, CounterWrap, NotificationButtonText, NotificationButtonWrap, NotificationCancelledLabel, NotificationContent, NotificationEmpty, NotificationEmptyText, NotificationText, NotificationTitleWrap, NotificationWrap } from './index.styled'
import MainIcon from '../../../img/svg/main'
import Currency from '../../Elements/currency'
import { fetchNotificationsThunk, updateNotificationStatusThunk } from '../../../redux/thunk/restaurant/notification'
import { setRestaurantShowNotificationsModal } from '../../../redux/action/restaurant'
import { selectCompanyData } from '../../../redux/selector/system'
import { confirmWaiterCallThunk } from '../../../redux/thunk/restaurant/waiterCall'
import { getFormattedCreatedDate } from './helper'
import { selectTablesNotifications, selectTablesWaiterCallsNotifications } from '../../../redux/selector/restaurant'
import { theme } from '../../../style/theme'
import { useWindowBreakPoint } from '../../../hooks/useWindowBreakPoint'
import { getTableTimerConfigsThunk } from '../../../redux/thunk/restaurantSettings'
import { splitTime } from '../../../helper'
import { updateWaiterNotificationWaiterComingThunk } from '../../../redux/thunk/waiterCalls'

const NotificationModal = React.memo(() => {
	const { t } = useTranslation()
	const notifications = useSelector(selectTablesNotifications)
	const showNotificationsModal = useSelector(state => state.restaurant.showNotificationsModal)
	const notificationsLength = useSelector(state => state.restaurant.tablesNotificationsLength)
	const waiterNotificationsCount = useSelector(state => state.restaurant.tablesWaiterNotificationsCount)
	const tableTimerConfigs = useSelector(state => state.restaurantSettings.tableTimerConfigs)
	const companyCountryCode = useSelector(selectCompanyData('countryCode'))
	const waiterNotifications = useSelector(selectTablesWaiterCallsNotifications)
	const dispatch = useDispatch()
	const isTablet = useWindowBreakPoint(theme.point820)
	const splitTimerLimit = splitTime(tableTimerConfigs?.timerLimit || '')
	const splitTimerHours = Number(splitTimerLimit?.hours)
	const splitTimerMinutes = Number(splitTimerLimit?.minutes)

	useEffect(() => {
		dispatch(fetchNotificationsThunk(true))
		let interval = setInterval(() => {
			dispatch(fetchNotificationsThunk())
		}, 5000)
		return () => clearInterval(interval)
	}, [dispatch])

	useEffect(() => {
		dispatch(getTableTimerConfigsThunk())
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (waiterNotifications?.length) {
			if (waiterNotificationsCount !== waiterNotifications?.length && !isTablet) {
				dispatch(setRestaurantShowNotificationsModal(true))
			}
		}
	}, [waiterNotifications?.length, isTablet, waiterNotificationsCount, dispatch])

	const handleClose = () => {
		dispatch(setRestaurantShowNotificationsModal(false))
	}

	const handleChangeStatus = (item) => {
		if (item.type === 'GUEST_ASK_WAITER') {
			dispatch(confirmWaiterCallThunk(item?.publicTableId))
		}
		if (item.type === 'WAITER_TO_KITCHEN_IS_CALLED') {
			dispatch(updateWaiterNotificationWaiterComingThunk(item.waiterNotificationRefId, { waiterComing: true }))
		}
		dispatch(updateNotificationStatusThunk(item.id, 'DELETED'))
	}

	return (
		<CustomRightModal id="notifications-modal"
											isOpen={showNotificationsModal}
											close={handleClose}
											title={<NotificationModalTitle notificationsLength={notificationsLength} />}
											className="with-header dark">
			{notifications?.length > 0 ? notifications?.map((el, index) =>
				<NotificationWrap key={index} data-testid="waiter-notification">
					{el.type === 'GUEST_ASK_WAITER' &&
						<>
							<NotificationContent><MainIcon icon="bell" />
								<NotificationText>
									<span>{getFormattedCreatedDate(el.createdDate, companyCountryCode)} </span>
									{t('restaurant.notificationsModal.waiterCall.message', { tableNumber: el.tableNumber, areaName: el.areaName })}
									{el.reasonType && (
										<span>{`: ${t(`restaurant.notificationsModal.waiterCall.reasons.${el.reasonType}`)}`}</span>
										)}
								</NotificationText>
							</NotificationContent>
							<NotificationButtonWrap onClick={() => handleChangeStatus(el)}>
								<MainIcon icon="tableWaiter" />
								<NotificationButtonText>{t('restaurant.waiterComing')}</NotificationButtonText>
							</NotificationButtonWrap>
						</>
						}
					{['GUEST_PAYED_ORDER_AND_PAY', 'ORDER_TIMER_LIMIT'].includes(el.type) &&
						<>
							{el.type === 'GUEST_PAYED_ORDER_AND_PAY' && <NotificationContent>
								<MainIcon icon="coins" />
								<NotificationText>
									<span>{getFormattedCreatedDate(el.createdDate, companyCountryCode)} </span>
									{t('restaurant.notificationsModal.payTable', { tableNumber: el.tableNumber, areaName: el.areaName })}
									<Currency>{el?.amount}</Currency>
								</NotificationText>
							</NotificationContent>}
							{el.type === 'ORDER_TIMER_LIMIT' && <NotificationContent>
								<MainIcon icon="timer" />
								<NotificationText>
									<span>{getFormattedCreatedDate(el.createdDate, companyCountryCode)} </span>
									{splitTimerHours === 0 ? t('restaurant.notificationsModal.orderTimerLimit',
												{ areaName: el.areaName, tableNumber: el.tableNumber, minutes: splitTimerMinutes })
											: t('restaurant.notificationsModal.orderTimerLimitWithHours',
												{ areaName: el.areaName, tableNumber: el.tableNumber, hours: splitTimerHours, minutes: splitTimerMinutes })}
								</NotificationText>
							</NotificationContent>}
							<NotificationButtonWrap onClick={() => handleChangeStatus(el)} data-testid="delete">
								<MainIcon icon="white-cross" />
							</NotificationButtonWrap>
						</>
						}
					{['WAITER_TO_KITCHEN_IS_CALLED', 'WAITER_TO_KITCHEN_IS_CANCELLED'].includes(el.type) &&
						<>
							{el.type === 'WAITER_TO_KITCHEN_IS_CALLED' && <>
								<NotificationContent>
									<MainIcon icon="chef-hat" />
									<NotificationText>
										<span>{getFormattedCreatedDate(el.createdDate, companyCountryCode)} </span>
										{t('restaurant.notificationsModal.kitchenIsCalled', { waiterUsername: el.waiterUsername, kitchenMonitorTitle: el.kitchenMonitorTitle })}
									</NotificationText>
								</NotificationContent>
								<NotificationButtonWrap onClick={() => handleChangeStatus(el)}>
									<MainIcon icon="tableWaiter" />
									<NotificationButtonText>{t('restaurant.waiterComing')}</NotificationButtonText>
								</NotificationButtonWrap>
							</>}
							{el.type === 'WAITER_TO_KITCHEN_IS_CANCELLED' &&
								<>
									<NotificationContent className="cancelled">
										<MainIcon icon="chef-hat" />
										<NotificationText>
											<span>{getFormattedCreatedDate(el.createdDate, companyCountryCode)} </span>
											{t('restaurant.notificationsModal.kitchenIsCancelled', { kitchenMonitorTitle: el.kitchenMonitorTitle })}
										</NotificationText>
									</NotificationContent>
									<NotificationButtonWrap onClick={() => handleChangeStatus(el)} data-testid="delete">
										<MainIcon icon="white-cross" />
									</NotificationButtonWrap>
								</>
								}

						</>
						}
					{el.type === 'GUEST_WAITER_CALL_CANCELLED' &&
						<>
							<NotificationContent className="cancelled">
								<MainIcon icon="bell" />
								<NotificationText>
									<span>{getFormattedCreatedDate(el.createdDate, companyCountryCode)} </span>
									<NotificationCancelledLabel>{t('restaurant.notificationsModal.waiterCall.cancelled')}</NotificationCancelledLabel>{' '}
									{t('restaurant.notificationsModal.waiterCall.cancelledMessage', { tableNumber: el.tableNumber, areaName: el.areaName })}
									{el.reasonType && (
										<span>{`: ${t(`restaurant.notificationsModal.waiterCall.reasons.${el.reasonType}`)}`}</span>
										)}
								</NotificationText>
							</NotificationContent>
							<NotificationButtonWrap onClick={() => handleChangeStatus(el)} data-testid="delete">
								<MainIcon icon="white-cross" />
							</NotificationButtonWrap>
						</>
						}
				</NotificationWrap>)
				: <NotificationEmpty>
					<MainIcon icon="bell" />
					<NotificationEmptyText>{t('restaurant.notificationsModal.empty')}</NotificationEmptyText>
				</NotificationEmpty>}
		</CustomRightModal>
	)
})

const NotificationModalTitle = React.memo(({ notificationsLength }) => {
	const { t } = useTranslation()
	const layout = (notificationsLength > 0 ? 'active ' : '') + 'modal'
	return (
		<NotificationTitleWrap>
			<div>{t('restaurant.notificationsModal.title')}</div>
			<CounterWrap data-testid="counter" className={layout}>
				<CounterText className={layout}>{notificationsLength}</CounterText>
			</CounterWrap>
		</NotificationTitleWrap>
	)
})
NotificationModalTitle.displayName = 'NotificationModalTitle'

NotificationModal.displayName = 'NotificationModal'
export default NotificationModal
